// Real-time lab ↔ clinic chat for one case. Used by BOTH sides — the clinic portal and the staff
// case workspace — so the thread is literally the same conversation, not two mirrored inboxes.
//
// Live delivery rides the existing SSE bus: the server publishes `workflow.message` scoped to the
// owning clinic, so a clinic stream only ever receives its own threads. Messages the tab sent itself
// are already in state, so incoming events are de-duplicated by id.
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDateTime, type WfPortalMessage } from '@alignova/shared';
import { useLiveEvent } from '../lib/useLive.ts';

export function CaseChat({
  caseId,
  side,
  load,
  send,
  className = '',
}: {
  caseId: string;
  /** Which side THIS user is on — decides message alignment and the "you" styling. */
  side: 'clinic' | 'lab';
  load: (caseId: string) => Promise<WfPortalMessage[]>;
  send: (caseId: string, body: string) => Promise<WfPortalMessage>;
  className?: string;
}) {
  const [messages, setMessages] = useState<WfPortalMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(() => {
    load(caseId)
      .then(setMessages)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [caseId, load]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Live: append anything arriving for THIS case that we don't already have.
  useLiveEvent<{ caseId: string; message: WfPortalMessage }>('workflow.message', (p) => {
    if (!p || p.caseId !== caseId) return;
    setMessages((prev) => (prev.some((m) => m.id === p.message.id) ? prev : [...prev, p.message]));
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [messages.length]);

  async function submit() {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    try {
      const m = await send(caseId, body);
      setDraft('');
      setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`flex flex-col rounded-lg border border-line bg-surface ${className}`}>
      <div className="min-h-[16rem] flex-1 space-y-2 overflow-y-auto p-3" style={{ maxHeight: '26rem' }}>
        {messages.map((m) => {
          const mine = m.side === side;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 ${mine ? 'bg-primary text-primary-fg' : 'bg-surface-2 text-ink'}`}>
                <div className={`text-[11px] ${mine ? 'text-primary-fg/70' : 'text-muted'}`}>
                  {m.authorName ?? (m.side === 'lab' ? 'Lab' : 'Clinic')} · {formatDateTime(m.createdAt)}
                </div>
                <div className="whitespace-pre-wrap text-sm">{m.body}</div>
              </div>
            </div>
          );
        })}
        {!messages.length && (
          <p className="py-8 text-center text-sm text-muted">
            No messages yet. {side === 'clinic' ? 'Ask the lab anything about this case.' : 'Message the clinic about this case.'}
          </p>
        )}
        <div ref={endRef} />
      </div>
      {error && <div className="px-3 pb-1 text-xs text-status-urgent-fg">{error}</div>}
      <div className="flex items-end gap-2 border-t border-line p-2">
        <textarea
          className="min-h-[2.5rem] flex-1 resize-y rounded border border-line bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent/30"
          rows={1}
          value={draft}
          placeholder="Write a message…"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter makes a new line.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy || !draft.trim()}
          className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-fg transition hover:opacity-90 disabled:opacity-40"
        >
          {busy ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
